import { NgModule } from "@angular/core";
import { CommonModule, NgOptimizedImage } from "@angular/common";
import { TermPipe } from "@/_pipes/term.pipe";
import { PagesComponent } from "@/components/pages/pages.component";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { RouterModule, Routes } from "@angular/router";
import { ReactiveFormsModule } from "@angular/forms";
import { SubscriptionGuard } from "@/_guards/subscription-guard.service";
import { StatisticsComponent } from "@/components/statistics/statistics.component";
import { DesktopMenuComponent } from "@/components/pages/desktop-menu/desktop-menu.component";
import { MobileBottomNavComponent } from "@/components/pages/mobile-bottom-nav/mobile-bottom-nav.component";
import { MobileContextualBarComponent } from "@/components/pages/mobile-contextual-bar/mobile-contextual-bar.component";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSidenavModule } from "@angular/material/sidenav";
import { InsightsFilterTopbarComponent } from "@/components/statistics/insights-filter-topbar/insights-filter-topbar.component";
import { InsightsFilterSidebarComponent } from "@/components/statistics/insights-filter-sidebar/insights-filter-sidebar.component";
import { HomeComponent } from "@/components/home/home.component";
import { HomeTaskListComponent } from "@/components/home/home-task-list/home-task-list.component";
import { HomeTaskDetailComponent } from "@/components/home/home-task-detail/home-task-detail.component";
import { PipelineStageComponent } from "@/components/home/pipeline-stage/pipeline-stage.component";
import { ReportSubmitComponent } from "@/components/shared/report-submit/report-submit.component";
import { CurrentUserResolver } from "@/resolver/CurrentUserResolver";
import { PerformanceOverviewComponent } from "../statistics/performance-overview/performance-overview.component";
import { SetupGuideButtonComponent } from "@/components/shared/setup-guide-button/setup-guide-button.component";
import { TasksFeatureGuard } from "@/_guards/tasks-feature.guard";
import { PerformanceAssuranceComponent } from "@/components/statistics/performance-assurance/performance-assurance.component";
import { PerformanceRiskManagementComponent } from "@/components/statistics/performance-risk-management/performance-risk-management.component";
import { PerformancePolicyComponent } from "@/components/statistics/performance-policy/performance-policy.component";
import { PerformanceTabComponent } from "@/components/statistics/performance-tab/performance-tab.component";
import { PerformancePromotionComponent } from "@/components/statistics/performance-promotion/performance-promotion.component";
import { IssueListFiltersModule } from "@/components/safety-assurance/issue-list/issue-list-filters.module";
import { RiseAgentSidebarComponent } from "@/components/rise-agent/rise-agent-sidebar.component";
import { RiseAgentIconComponent } from "@/components/rise-agent/rise-agent-icon.component";
import { RiseAgentChartComponent } from "@/components/rise-agent/rise-agent-chart/rise-agent-chart.component";
import { RiseAgentMobileFabComponent } from "@/components/rise-agent/rise-agent-mobile-fab.component";
import { MobileOptionsPanelComponent } from "@/components/shared/mobile-options-panel/mobile-options-panel.component";
import { SidebarModule } from "primeng/sidebar";
import { MarkdownPipe } from "@/_pipes/markdown.pipe";
import { TooltipModule } from "primeng/tooltip";
import { MenuModule } from "primeng/menu";
import { NotAllowedComponent } from "@/components/shared/not-allowed/not-allowed.component";
import { RECORD_UNAVAILABLE_PATH } from "@/components/shared/not-allowed/record-unavailable";

const ROUTES: Routes = [
    {
        path: "",
        component: PagesComponent,
        children: [
            {
                path: "performance",
                component: StatisticsComponent,
                canActivate: [SubscriptionGuard],
                children: [
                    {
                        path: "",
                        pathMatch: "full",
                        redirectTo: "reports",
                    },
                    {
                        path: "reports",
                        component: PerformanceOverviewComponent,
                    },
                    {
                        path: "risk-management",
                        component: PerformanceRiskManagementComponent,
                    },
                    {
                        path: "assurance",
                        component: PerformanceAssuranceComponent,
                    },
                    {
                        path: "promotions",
                        component: PerformancePromotionComponent,
                    },
                    {
                        path: "policy",
                        component: PerformancePolicyComponent,
                    },
                    {
                        path: "**",
                        redirectTo: "reports",
                    },
                ],
            },
            {
                path: "home",
                component: HomeComponent,
                canActivate: [SubscriptionGuard],
                children: [
                    {
                        path: "",
                        pathMatch: "full",
                        redirectTo: "tasks",
                    },
                    {
                        path: "tasks",
                        component: HomeTaskListComponent,
                        resolve: {
                            user: CurrentUserResolver,
                        },
                    },
                    {
                        path: "tasks/:taskId",
                        component: HomeTaskDetailComponent,
                        canActivate: [TasksFeatureGuard],
                    },
                    {
                        path: "submit",
                        component: ReportSubmitComponent,
                        canActivate: [SubscriptionGuard],
                    },
                ],
            },
            {
                // Its own page rather than a child of home: it replaces the
                // dashboard rather than sitting inside its left column.
                path: "pipeline/:stage",
                component: PipelineStageComponent,
                canActivate: [SubscriptionGuard],
            },
            {
                path: "risk-management",
                loadChildren: () =>
                    import("../risk-management/risk-management.module").then(
                        (m) => m.RiskManagementModule,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "assurance",
                loadChildren: () =>
                    import("../safety-assurance/safety-assurance.module").then(
                        (m) => m.SafetyAssuranceModule,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "policies",
                loadChildren: () =>
                    import("../safety-policy/safety-policy.module").then(
                        (m) => m.SafetyPolicyModule,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "promotions",
                loadChildren: () =>
                    import(
                        "@/components/safety-promotion/safety-promotion.module"
                    ).then((m) => m.SafetyPromotionModule),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "manuals",
                loadChildren: () =>
                    import("../manuals/manuals.module").then(
                        (m) => m.ManualsModule,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "assessments",
                loadChildren: () =>
                    import("../assessment/assessment.module").then(
                        (m) => m.AssessmentModule,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "reports-v2",
                loadChildren: () =>
                    import("../reports/reports-v2.module").then(
                        (m) => m.ReportsV2Module,
                    ),
                canActivate: [SubscriptionGuard],
            },
            {
                path: "profile",
                loadChildren: () =>
                    import("../profile/profile.module").then(
                        (m) => m.ProfileModule,
                    ),
            },
            {
                path: RECORD_UNAVAILABLE_PATH,
                component: NotAllowedComponent,
                data: {
                    hideDesktopMenu: true,
                    hideMobileMenu: true,
                },
            },
        ],
    },
];

@NgModule({
    declarations: [
        PagesComponent,
        DesktopMenuComponent,
        StatisticsComponent,
        InsightsFilterSidebarComponent,
        InsightsFilterTopbarComponent,
        MobileBottomNavComponent,
        MobileContextualBarComponent,
        PerformanceOverviewComponent,
        PerformanceAssuranceComponent,
        PerformanceRiskManagementComponent,
        PerformancePromotionComponent,
        RiseAgentSidebarComponent,
        RiseAgentIconComponent,
        RiseAgentChartComponent,
        MarkdownPipe,
    ],
    imports: [
        CommonModule,
        TermPipe,
        RouterModule,
        SharedComponentsModule,
        RouterModule.forChild(ROUTES),
        ReactiveFormsModule,
        MatExpansionModule,
        MatSidenavModule,
        PerformanceTabComponent,
        NgOptimizedImage,
        SetupGuideButtonComponent,
        IssueListFiltersModule,
        HomeTaskListComponent,
        HomeTaskDetailComponent,
        SidebarModule,
        RiseAgentMobileFabComponent,
        MobileOptionsPanelComponent,
        TooltipModule,
        MenuModule,
    ],
    exports: [],
})
export class PagesModule {}
